import { useMemo, useState, type ReactNode } from 'react';

import { Button } from '../../shared/ui/button';
import { CodeView } from '../../shared/ui/code-view';
import { PlayIcon } from '../../shared/ui/icons';
import { PageHeader } from '../../shared/ui/layout';
import { Note } from '../../shared/ui/note';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import { InfoDot } from '../../shared/ui/tooltip';
import { pointTime } from '../../shared/lib/format';
import { bytesWords, holdersWords, isDraft, type IPin } from '../../entities/transform';
import type { TLibraryItem, TLibraryVersionSummary } from '../../entities/library';
import { useSession } from '../../entities/session';
import { LibraryCompare } from '../../features/library-compare';
import { LibraryDelete } from '../../features/library-delete';
import { LibraryPublish } from '../../features/library-publish';
import { LibraryVersions } from '../../features/library-versions';
import { TransformDryRun } from '../../features/transform-dry-run';
import { UsedInFlows } from '../../widgets/used-in-flows';
import { useVersionBody } from './use-library-item';

// "Used in 1 flow, in a draft": a draft pin runs nowhere yet, and the header says so.
export const headerFacts = (
  item: TLibraryItem,
  pins: IPin[],
  now: number,
  timeZone?: string,
): string => {
  const live = pins.filter((pin) => pin.holderState !== 'draft');
  const where =
    pins.length === 0
      ? 'No step pins it yet'
      : live.length === 0
        ? `Used in ${holdersWords(pins)}, in a draft`
        : `Used in ${holdersWords(live)}`;
  return `${where} · last edited ${pointTime(item.updatedAt, now, timeZone)}`;
};

export const CodeItemScreen = ({
  item,
  versions,
  pins,
  breadcrumb,
  onDeleted,
}: {
  item: TLibraryItem;
  versions: TLibraryVersionSummary[];
  pins: IPin[];
  breadcrumb: ReactNode;
  onDeleted: () => void;
}) => {
  const [selected, setSelected] = useState(item.latestVersion);
  const [trying, setTrying] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  // Null means "showing what the server has". A string means somebody typed.
  const [edit, setEdit] = useState<string | null>(null);

  const timeZone = useSession().data?.preferences.timezone;
  const now = useMemo(() => Date.now(), [item.id, item.updatedAt]);
  const source = useVersionBody(item.id, selected);
  const draft = isDraft(selected, item.latestVersion, pins);
  const saved = source.data?.code ?? '';

  const show = (version: number) => {
    setSelected(version);
    setEdit(null);
  };

  const dirty = edit !== null && source.data !== undefined && edit !== saved;

  return (
    <>
      <PageHeader
        title={item.name}
        breadcrumbs={breadcrumb}
        badge={
          <span className="flex items-center gap-2 font-mono text-micro text-faint">
            code · v{selected}
            {draft ? ' · draft' : ''}
            {dirty ? ' · edited' : ''}
            <InfoDot side="bottom">
              A version runs only where a Code step in a flow pins it. Publishing changes nothing on
              its own.
            </InfoDot>
          </span>
        }
        description={headerFacts(item, pins, now, timeZone)}
        actions={
          <>
            <LibraryDelete item={item} onDeleted={onDeleted} />
            <Button size="sm" variant="ghost" onClick={() => setTrying(true)}>
              <PlayIcon size={12} /> Try it on a sample
            </Button>
            <Button size="sm" disabled={!dirty} onClick={() => setPublishing(true)}>
              Publish v{item.latestVersion + 1}
            </Button>
          </>
        }
      />

      <div className="flex w-full max-w-content items-start gap-5 p-page">
        <div className="min-w-0 flex-[1.5]">
          <div className="mb-2 flex items-center gap-inset">
            <span className="font-mono text-micro text-faint">code.js</span>
            <span className="ml-auto font-mono text-micro text-faint">
              {source.data ? bytesWords(source.data.size) : ''}
              {source.data?.runtimeKind ? ` · ${source.data.runtimeKind}` : ''}
            </span>
          </div>

          {source.isError && (
            <ErrorState error={source.error} onRetry={() => void source.refetch()} />
          )}
          {source.isPending && <LoadingState rows={12} />}
          {source.data && (
            // Keyed by version: the editor refuses an outside value while focused, so another row needs a new one.
            <CodeView
              key={selected}
              value={edit ?? saved}
              language="javascript"
              onChange={setEdit}
              className="max-h-body rounded-panel"
            />
          )}

          {dirty && (
            <Note className="mt-4">
              Edited against v{selected}, not saved. Publishing writes v{item.latestVersion + 1};
              picking another version on the right discards it.
            </Note>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-5">
          <LibraryVersions
            item={item}
            versions={versions}
            pins={pins}
            selected={selected}
            onSelect={show}
            onCompare={() => setComparing(true)}
            now={now}
          />
          <UsedInFlows kind="transform" id={item.id} name={item.name} timeZone={timeZone} />
        </div>
      </div>

      <TransformDryRun
        transform={item}
        versions={versions}
        pins={pins}
        open={trying}
        onOpenChange={setTrying}
      />
      <LibraryCompare
        // A new version resets the pair to "newest against the one before".
        key={versions[0]?.version}
        item={item}
        versions={versions}
        pins={pins}
        open={comparing}
        onOpenChange={setComparing}
      />
      <LibraryPublish
        item={item}
        body={{ code: edit ?? '' }}
        basedOn={selected}
        pins={pins}
        open={publishing}
        onOpenChange={setPublishing}
        onPublished={show}
      />
    </>
  );
};
